import {FetchService} from "dc-services/fetch.service";

/**
 * Enhanced search tracking.
 *
 * Sends the search term, the customer number and the result page of the current
 * search result page to the backend extension. The backend stores the request in
 * shop_turbo_search_log, and Business Central syncs the rows.
 *
 * The search result page carries the search term in the "input_search" URL parameter
 * and the result page in the "page" URL parameter (see search/?input_search=...&page=N).
 * The customer number comes from the data-customer-no attribute of <body>.
 */

const LOG_ENDPOINT = '/em/searchTracking/logNewEntry';
const SEARCH_TERM_PARAM = 'input_search';
const PAGE_PARAM = 'page';
const DEFAULT_PAGE_NUMBER = 1;

/**
 * Log the search request of the current page. Does nothing when the page is not a search result page.
 * A failed tracking call must never break the page, so errors are only reported to the console.
 */
export async function logSearchRequest() {
    const searchData = collectDataFromCurrentPage();

    if (!searchData) {
        return;
    }

    try {
        const response = await FetchService.post(LOG_ENDPOINT, searchData);

        if (!response.success) {
            console.warn('Search tracking rejected the request:', response.message);
        }
    } catch (error) {
        console.warn('Search tracking request failed:', error);
    }
}

/**
 * Collect the tracking payload from the URL and the <body> element.
 *
 * @returns {{searchTerm: string, customerNo: string, pageNumber: number}|null}
 *          The payload, or null when the URL has no search term
 */
function collectDataFromCurrentPage() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = (urlParams.get(SEARCH_TERM_PARAM) ?? '').trim();

    if (searchTerm === '') {
        return null;
    }

    return {
        searchTerm,
        customerNo: document.body?.dataset.customerNo ?? '',
        pageNumber: parsePageNumber(urlParams.get(PAGE_PARAM)),
    };
}

/**
 * Parse the page parameter. Missing or invalid values fall back to the first page.
 *
 * @param {string|null} rawPage The raw "page" URL parameter
 * @returns {number} A positive integer
 */
function parsePageNumber(rawPage) {
    const pageNumber = Number.parseInt(rawPage ?? '', 10);

    return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : DEFAULT_PAGE_NUMBER;
}
